import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { generateSmartNotifications, SmartNotification, NotificationLevel } from '@/lib/smart-notifications';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, AlertTriangle, AlertCircle, CheckCircle2, Info, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const levelConfig: Record<NotificationLevel, { icon: typeof Bell; className: string; bg: string }> = {
  danger: { icon: AlertCircle, className: 'text-destructive', bg: 'bg-destructive/10' },
  warning: { icon: AlertTriangle, className: 'text-orange-500', bg: 'bg-orange-500/10' },
  success: { icon: CheckCircle2, className: 'text-success', bg: 'bg-success/10' },
  info: { icon: Info, className: 'text-primary', bg: 'bg-primary/10' },
};

export function SmartNotifications() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const notifications = useMemo(() => generateSmartNotifications(data), [data]);

  const visible = notifications.filter(n => !dismissed.has(n.id));

  if (visible.length === 0) return null;

  const shown = visible.slice(0, 5);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Smart Alerts</h3>
          <span className="ml-auto rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
            {visible.length}
          </span>
        </div>

        <div className="space-y-2">
          {shown.map(notification => {
            const config = levelConfig[notification.level];
            const Icon = config.icon;

            return (
              <div
                key={notification.id}
                className={cn(
                  'flex items-start gap-2.5 rounded-lg p-2.5 transition-colors',
                  config.bg
                )}
              >
                <div className={cn('mt-0.5 shrink-0', config.className)}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{notification.title}</p>
                  <p className="text-[11px] text-muted-foreground">{notification.message}</p>
                  {notification.actionRoute && (
                    <button
                      className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium text-primary"
                      onClick={() => navigate(notification.actionRoute!)}
                    >
                      {notification.actionLabel}
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <button
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setDismissed(prev => new Set(prev).add(notification.id))}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        {visible.length > 5 && (
          <p className="mt-2 text-center text-[10px] text-muted-foreground">
            +{visible.length - 5} more alerts
          </p>
        )}
      </CardContent>
    </Card>
  );
}
